import type { AltTextStatus } from '../product-image-alt-text';
/**
 * Shopify catalog media row (product + variant assignment annotations).
 * Variant-details page contracts live in variant-details.ts — re-exported here
 * for backward-compatible imports.
 */

/** Shopify media kinds, as the media list filters them. */
export const MEDIA_CONTENT_TYPES = ['IMAGE', 'VIDEO', 'EXTERNAL_VIDEO', 'MODEL_3D'] as const;

export type MediaContentType = (typeof MEDIA_CONTENT_TYPES)[number];

export function isMediaContentType(value: string): value is MediaContentType {
  return (MEDIA_CONTENT_TYPES as readonly string[]).includes(value);
}

/** The media list's file-size filter bound in KB (50 MB). */
export const MEDIA_FILE_SIZE_KB_MAX = 51200;

/** The media list's shortest-side filter bound in pixels. */
export const MEDIA_DIMENSION_PX_MAX = 10000;

/** File types the Media page filters by (its mime type), with their labels. */
export const MEDIA_FILE_TYPES = [
  { value: 'image/jpeg', label: 'JPEG' },
  { value: 'image/png', label: 'PNG' },
  { value: 'image/webp', label: 'WebP' },
  { value: 'image/gif', label: 'GIF' },
  { value: 'video/mp4', label: 'MP4' },
  { value: 'video/quicktime', label: 'MOV' },
  { value: 'video/x-m4v', label: 'M4V' },
] as const;

export function isMediaFileType(value: string): boolean {
  return MEDIA_FILE_TYPES.some((t) => t.value === value);
}

export type ShopifyCatalogMedia = {
  /** jflo's product_media row id, for editing the image (T-142). */
  id?: string;
  shopify_media_refid: string;
  media_content_type: string | null;
  status: string | null;
  alt_text: string | null;
  image_url: string | null;
  preview_image_url: string | null;
  width: number | null;
  height: number | null;
  file_size: number | null;
  mime_type: string | null;
  format: string | null;
  filename: string | null;
  duration_ms: number | null;
  embed_url: string | null;
  origin_url: string | null;
  host: string | null;
  shopify_image_refid: string | null;
  shopify_file_status: string | null;
  shopify_created_at: string | null;
  shopify_updated_at: string | null;
  product_position: number | null;
  assigned_to_variant: boolean;
  variant_position: number | null;
  /** Images only, where loaded (the product page): where its alt text stands (T-165). */
  alt_status?: AltTextStatus | null;
  /** jflo's suggestion waiting for review. */
  alt_suggested?: string | null;
};

export type {
  VariantDetailsResponse,
  VariantDetailsVariant,
  VariantDetailsProduct,
  VariantDetailsXeroItem,
} from './variant-details';
